import React, { useState, useEffect } from 'react';
import { 
  BanknotesIcon,
  DocumentTextIcon, 
  UserGroupIcon, 
  ArrowUpTrayIcon,
  PlusIcon,
  SparklesIcon,
  CalendarDaysIcon,
  CogIcon,
  ArrowLeftOnRectangleIcon,
  ArrowDownTrayIcon,
  MusicalNoteIcon
} from '@heroicons/react/24/outline';
import EntikRecordsReservas from './EntikRecordsReservas';
import KitDigitalPanel from './KitDigitalPanel';
import WooCommercePanel from './WooCommercePanel';
import { shouldShowEntikRecordsTab } from '../utils/entikRecordsAccess';
import { shouldShowKitDigitalTab } from '../utils/kitDigitalAccess';
import { DashboardStats } from './DashboardStats';
import { InvoiceList, BulkDeleteResult } from './InvoiceList';
import { ClientList } from './ClientList';
import { ImportDocumentsDropzone } from './ImportInvoicesDropzone';
import { InvoiceForm } from './InvoiceForm';
import { ProfessionalExpensesManager } from './ProfessionalExpensesManager';
import { buildInvoiceApiPayload } from '../utils/invoiceApi';
import { AISettings } from './AISettings';
import { AutoBillingManager } from './AutoBillingManager';
import { AutoBillingStatus } from './AutoBillingStatus';
import { SystemMonitor } from './SystemMonitor';
import { InvoicePreview } from './InvoicePreview';
import { QuarterlyTaxStats } from './QuarterlyTaxStats';
import { Settings } from './Settings';
import { AppShell, buildDefaultNavItems } from './AppShell';
import { CommandAction } from './CommandPalette';
import { DashboardHome } from './DashboardHome';
import { EmailApp } from './EmailApp';
import { UserManagement } from './UserManagement';
import { PlatformUsersPanel } from './PlatformUsersPanel';
import { BackupRestore } from './BackupRestore';
import Login from './Login';
import { ChangePassword } from './ChangePassword';
import { Invoice, Client, User, Company, AppState, BackupData, AuthResponse, Project } from '../types';
import { SkeletonInvoiceList, SkeletonKpiRow, SkeletonChart } from './SkeletonLoader';
import { GestoriaHub } from './GestoriaHub';
import { AuthService } from '../services/authService';
import { apiService } from '../services/apiService';
import { AssistantProvider, useAssistant } from '../context/AssistantContext';
import { FactuAssistant } from './FactuAssistant';

export const Dashboard: React.FC<{ onLogout?: () => void }> = ({ onLogout }) => {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [wooCommerceEnabled, setWooCommerceEnabled] = useState(false);
  const [wcOauthState, setWcOauthState] = useState<string | null>(null);
  const [wcOauthStore, setWcOauthStore] = useState<string | null>(null);
  const [settingsSection, setSettingsSection] = useState('email');
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [rememberMe, setRememberMe] = useState(false);
  
  // Application state with multi-tenant support
  const [appState, setAppState] = useState<AppState>({
    currentUser: null,
    currentCompany: null,
    companies: [],
    users: [],
    invoices: [],
    clients: [],
    isLoading: false,
    isAuthenticated: false
  });

  const [activeTab, setActiveTab] = useState('dashboard');
  const [nextInvoiceNumber, setNextInvoiceNumber] = useState(1);
  
  // Función para obtener el siguiente número de factura para la empresa actual
  const getNextInvoiceNumber = (company: Company | null) => {
    if (!company) return 1;
    
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1; // 1-12
    
    // Si es un nuevo año o mes de inicio fiscal, reiniciar numeración
    if (company.currentYear !== currentYear || 
        (company.fiscalYearStart && currentMonth === company.fiscalYearStart)) {
      return 1;
    }
    
    return company.lastInvoiceNumber + 1;
  };
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [showAISettings, setShowAISettings] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState<Invoice | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dashboardApiStats, setDashboardApiStats] = useState<{ expenseCount?: number; expenseTotal?: number; receivedInvoiceCount?: number } | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [assistantOpen, setAssistantOpen] = useState(false);

  // Bootstrap default user and load saved state
  useEffect(() => {
    AuthService.initializeDefaultUser();
    const savedState = localStorage.getItem('dashboard-app-state');
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        // Convert date strings back to Date objects
        const convertedState = {
          ...parsedState,
          companies: parsedState.companies.map((company: any) => ({
            ...company,
            createdAt: new Date(company.createdAt),
            updatedAt: new Date(company.updatedAt)
          })),
          users: parsedState.users.map((user: any) => ({
            ...user,
            createdAt: new Date(user.createdAt),
            updatedAt: new Date(),
            lastLogin: user.lastLogin ? new Date(user.lastLogin) : undefined
          })),
          invoices: parsedState.invoices.map((invoice: any) => ({
            ...invoice,
            date: new Date(invoice.date),
            dueDate: invoice.dueDate ? new Date(invoice.dueDate) : undefined,
            createdAt: new Date(invoice.createdAt),
            updatedAt: new Date(invoice.updatedAt),
            client: {
              ...invoice.client,
              createdAt: new Date(invoice.client.createdAt),
              updatedAt: new Date(invoice.client.updatedAt)
            }
          })),
          clients: parsedState.clients.map((client: any) => ({
            ...client,
            createdAt: new Date(client.createdAt),
            updatedAt: new Date()
          }))
        };
        setAppState(convertedState);
        
        // Set current invoice number
        const maxInvoiceNumber = Math.max(0, ...convertedState.invoices.map((inv: Invoice) => inv.number));
        setNextInvoiceNumber(maxInvoiceNumber + 1);
      } catch (error) {
        console.error('Error loading saved state:', error);
        initializeDefaultState();
      }
    } else {
      initializeDefaultState();
    }
  }, []);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (appState.companies.length > 0) {
      localStorage.setItem('dashboard-app-state', JSON.stringify(appState));
    }
  }, [appState]);

  const initializeDefaultState = () => {
    const defaultCompany: Company = {
      id: 'company_default',
      name: 'Mi Empresa',
      cif: 'B00000000',
      address: 'Dirección de la empresa',
      city: 'Ciudad',
      postalCode: '00000',
      email: 'contacto@miempresa.com',
      phone: '+34 000 000 000',
      website: '',
      isActive: true,
      // Configuración de facturación
      invoicePrefix: 'FAC',
      currentYear: new Date().getFullYear(),
      lastInvoiceNumber: 0,
      fiscalYearStart: 1, // Enero
      createdAt: new Date(),
      updatedAt: new Date()
    };

    const defaultUser: User = {
      id: 'user_admin',
      email: 'admin@miempresa.com',
      name: 'Administrador',
      password: 'admin123', // Will be hashed by AuthService
      role: 'admin',
      companyId: defaultCompany.id,
      isActive: true,
      mustChangePassword: true,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    setAppState({
      currentUser: defaultUser,
      currentCompany: defaultCompany,
      companies: [defaultCompany],
      users: [defaultUser],
      invoices: [],
      clients: [],
      isLoading: false,
      isAuthenticated: false
    });
  };

  // Authentication functions
  const handleLoginSuccess = (response: AuthResponse) => {
    if (response && response.success) {
      setIsAuthenticated(true);
      if (rememberMe) localStorage.setItem('dashboard_remember_me', 'true');

      if (response.user && response.company) {
        const userOk: User = response.user as User;
        const companyOk: Company = response.company as Company;
        setCurrentUser(userOk);
        setCurrentCompany(companyOk);
        setAppState(prev => ({ ...prev, currentUser: userOk, currentCompany: companyOk, isAuthenticated: true }));
        loadUsers();
        loadCompanies();
        loadCompanyBillingData(companyOk.id);
      } else {
        apiService.getProfile().then((res) => {
          const prof = (res.data as any)?.user;
          if (prof) {
            const company = prof.company ? ({
              id: prof.company.id,
              name: prof.company.name,
              cif: prof.company.cif,
              address: '', city: '', postalCode: '', email: '', isActive: true,
              invoicePrefix: 'FAC', currentYear: new Date().getFullYear(), lastInvoiceNumber: 0, fiscalYearStart: 1,
              phone: '', website: '', logo: '', createdAt: new Date(), updatedAt: new Date()
            } as Company) : null;
            const userObj: User = {
              id: prof.id,
              email: prof.email,
              name: prof.username || prof.email,
              password: '',
              role: prof.role,
              companyId: prof.company?.id || '',
              isActive: true,
              createdAt: new Date(),
              updatedAt: new Date()
            } as User;
            setCurrentUser(userObj);
            setCurrentCompany(company);
            setAppState(prev => ({ ...prev, currentUser: userObj, currentCompany: company, isAuthenticated: true }));
          }
        }).finally(() => {
          loadUsers();
          loadCompanies();
        });
      }
    }
  };

  const handleLogout = () => {
    AuthService.logout();
    setIsAuthenticated(false);
    setCurrentUser(null);
    setCurrentCompany(null);
    setRememberMe(false);
    
    // Clear remember me
    localStorage.removeItem('dashboard_remember_me');
    
    // Reset app state
    setAppState(prev => ({
      ...prev,
      currentUser: null,
      currentCompany: null,
      isAuthenticated: false
    }));
    onLogout?.();
  };

  // ===== Carga de datos desde API =====
  const loadUsers = async () => {
    try {
      const res = await apiService.getUsers();
      if ((res as any).error) return;
      const apiUsers = (res.data as any)?.users || [];
      const users: User[] = apiUsers.map((u: any) => ({
        id: u.id,
        email: u.email,
        name: u.name || u.username || u.email,
        password: '',
        role: u.role || 'user',
        companyId: u.companyId || '',
        isActive: !!u.isActive,
        authorizedCompanyIds: u.authorizedCompanyIds || [],
        createdAt: new Date(),
        updatedAt: new Date()
      }));
      setAppState(prev => ({ ...prev, users }));
    } catch (e) {
      console.error('Error cargando usuarios', e);
    }
  };


  const mapApiClient = (row: any, companyId: string): Client => ({
    id: row.id,
    name: row.name,
    cif: row.cif || '',
    address: row.address || '',
    city: row.city || '',
    email: row.email || '',
    phone: row.phone || '',
    companyId,
    createdAt: new Date(row.created_at || row.createdAt || Date.now()),
    updatedAt: new Date(row.updated_at || row.updatedAt || Date.now()),
  });

  const buildInvoiceLineSummary = (row: any): string => {
    if (row.line_summary) return row.line_summary;
    const fromItems = (row.items || [])
      .map((it: any) => it.description)
      .filter(Boolean)
      .join(' · ');
    if (fromItems) return fromItems;
    const structured = [
      ...(row.services || []),
      ...(row.reimbursableExpenses || []),
      ...(row.suplidos || []),
    ]
      .map((l: any) => l.description)
      .filter(Boolean)
      .join(' · ');
    if (structured) return structured;
    const notes = String(row.notes || '').split('\n').find((l: string) => l.trim() && !l.startsWith('[') && !l.startsWith('Import PDF') && !l.startsWith('Import documento'));
    return notes || '';
  };

  const mapApiInvoice = (row: any, clients: Client[], companyId: string): Invoice => {
    const client = clients.find((c) => c.id === row.client_id) || {
      id: row.client_id,
      name: row.client_name || 'Cliente',
      cif: row.client_cif || '',
      address: row.client_address || '',
      city: row.client_city || '',
      email: row.client_email || '',
      phone: row.client_phone || '',
      companyId,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    const numPart = parseInt(String(row.number || '').split('-').pop() || '0', 10);
    const subtotal = Number(row.subtotal || 0);
    const ivaAmount = Number(row.iva_amount ?? row.ivaAmount ?? 0);
    const ivaPct = subtotal > 0 ? Math.round((ivaAmount / subtotal) * 100) : 21;
    const irpfAmount = Number(row.irpf_amount ?? row.irpfAmount ?? 0);
    const irpfPct = subtotal > 0 ? Math.round((irpfAmount / subtotal) * 100) : 0;
    const items = row.items || [];
    return {
      id: row.id,
      number: Number.isFinite(numPart) ? numPart : 0,
      displayNumber: row.number,
      date: new Date(row.date),
      clientId: row.client_id,
      client,
      companyId,
      projectId: row.project_id || row.projectId || null,
      groupIds: (row.groups || []).map((g: { id: string }) => g.id),
      groups: row.groups || [],
      description: buildInvoiceLineSummary(row),
      unitPrice: 0,
      quantity: 1,
      subtotal,
      suplidosAmount: Number(row.suplidos_amount ?? row.suplidosAmount ?? 0),
      ivaPercentage: [0, 4, 10, 21].includes(ivaPct) ? ivaPct : 21,
      ivaAmount,
      irpfPercentage: irpfPct,
      irpfAmount,
      total: Number(row.total || 0),
      status: row.status,
      dueDate: row.due_date ? new Date(row.due_date) : undefined,
      paymentMethod: row.payment_method,
      bankIban: row.bank_iban || undefined,
      bankHolder: row.bank_holder || undefined,
      bankName: row.bank_name || undefined,
      paymentNotes: row.payment_notes || undefined,
      notes: row.notes || '',
      services: items
        .filter((it: any) => !it.lineType || it.lineType === 'service')
        .map((it: any) => ({
          description: it.description,
          quantity: 1,
          unitPrice: Number(it.subtotal || 0),
          importe: Number(it.subtotal || 0),
        })),
      reimbursableExpenses: items
        .filter((it: any) => it.lineType === 'reimbursable_expense')
        .map((it: any) => ({
          description: it.description,
          importe: Number(it.subtotal || 0),
        })),
      suplidos: items
        .filter((it: any) => it.lineType === 'suplido')
        .map((it: any) => ({
          description: it.description,
          importe: Number(it.subtotal || 0),
        })),
      fiscalBreakdown: row.fiscalBreakdown,
      alreadyPresentedToAeat: !!(row.already_presented_aeat ?? row.alreadyPresentedToAeat),
      createdAt: new Date(row.created_at || Date.now()),
      updatedAt: new Date(row.updated_at || Date.now()),
    } as Invoice;
  };

  const mapDetailInvoice = (row: any, clients: Client[], companyId: string): Invoice => {
    const base = mapApiInvoice(row, clients, companyId);
    const mapLine = (l: any) => ({
      description: l.description,
      quantity: Number(l.quantity || 1),
      unitPrice: Number(l.unit_price ?? l.unitPrice ?? 0),
      importe: Number(l.importe ?? l.subtotal ?? 0),
      tipo: l.tipo,
    });
    const services =
      (row.services?.length ? row.services : null)?.map(mapLine) ||
      base.services ||
      [];
    const reimbursableExpenses = (row.reimbursableExpenses || row.reimbursable_expenses || []).map(
      mapLine
    );
    const suplidos = (row.suplidos || []).map(mapLine);
    return {
      ...base,
      description: buildInvoiceLineSummary({ ...row, services, reimbursableExpenses, suplidos }),
      services,
      reimbursableExpenses,
      suplidos,
      suplidosAmount: Number(row.suplidos_amount ?? row.suplidosAmount ?? base.suplidosAmount ?? 0),
      fiscalBreakdown: row.fiscalBreakdown || base.fiscalBreakdown,
      paymentMethod: row.payment_method || base.paymentMethod,
      dueDate: row.due_date ? new Date(row.due_date) : base.dueDate,
      ivaPercentage: Number(row.iva_percentage ?? base.ivaPercentage),
      irpfPercentage: Number(row.irpf_percentage ?? base.irpfPercentage),
    };
  };

  const loadProjects = async (companyId?: string) => {
    const cid = companyId || appState.currentCompany?.id;
    if (!cid || !apiService.isAuthenticated()) return;
    try {
      const res = await apiService.getProjects();
      const rows = (res.data as { projects?: Array<Record<string, string>> })?.projects || [];
      setProjects(
        rows.map((p) => ({
          id: p.id,
          companyId: p.company_id,
          name: p.name,
          slug: p.slug,
          color: p.color,
          brand: p.brand,
        }))
      );
    } catch {
      setProjects([]);
    }
  };

  const loadCompanyBillingData = async (companyId?: string, projectId?: string | null) => {
    const cid = companyId || appState.currentCompany?.id;
    const pid = projectId !== undefined ? projectId : currentProjectId;
    if (!cid || !apiService.isAuthenticated()) return;
    try {
      const [clientsRes, invoicesRes] = await Promise.all([
        apiService.getClients(),
        apiService.getInvoices({ limit: 500, ...(pid ? { projectId: pid } : {}) }),
      ]);
      const rawClients = Array.isArray(clientsRes.data) ? clientsRes.data : (clientsRes.data as any)?.clients || [];
      const clients = rawClients.map((c: any) => mapApiClient(c, cid));
      const rawInv = (invoicesRes.data as any)?.invoices || [];
      const invoices = rawInv.map((inv: any) => mapApiInvoice(inv, clients, cid));
      setAppState((prev) => ({
        ...prev,
        clients: clients.filter((c: Client) => c.companyId === cid),
        invoices: invoices.filter((i: Invoice) => i.companyId === cid),
      }));
      const maxNum = Math.max(0, ...invoices.map((i: Invoice) => i.number));
      if (maxNum > 0) setNextInvoiceNumber(maxNum + 1);
      const statsRes = await apiService.getDashboardStats(pid || undefined);
      if (!(statsRes as any).error && statsRes.data) {
        setDashboardApiStats((statsRes.data as any).stats || null);
      }
    } catch (e) {
      console.error('Error sincronizando facturas/clientes', e);
    }
  };

  const loadCompanies = async () => {
    try {
      const res = await apiService.getCompanies();
      if ((res as any).error) return;
      const apiCompanies = (res.data as any)?.companies || (res.data as any) || [];
      const apiCurrentCompany = (res.data as any)?.currentCompany;
      const companies: Company[] = (Array.isArray(apiCompanies) ? apiCompanies : []).map((c: any) => ({
        id: c.id,
        name: c.name,
        cif: c.cif,
        address: c.address || '',
        city: c.city || '',
        postalCode: c.postalCode || '',
        email: c.email || '',
        phone: c.phone || '',
        website: c.website || '',
        logo: c.logo || '',
        isActive: c.isActive !== undefined ? !!c.isActive : true,
        invoicePrefix: c.invoicePrefix || 'FAC',
        currentYear: c.currentYear || new Date().getFullYear(),
        lastInvoiceNumber: c.lastInvoiceNumber || 0,
        fiscalYearStart: c.fiscalYearStart || 1,
        paymentSettings: {
        bankIban: c.bank_iban || c.bankIban || '',
        bankHolder: c.bank_holder || c.bankHolder || '',
        bankName: c.bank_name || c.bankName || '',
        bankSwift: c.bank_swift || c.bankSwift || '',
        defaultTransferConcept: c.default_transfer_concept || c.defaultTransferConcept || '',
        defaultBizumPhone: c.default_bizum_phone || c.defaultBizumPhone || '',
        defaultCardText: c.default_card_text || c.defaultCardText || 'Pagado con tarjeta',
        defaultPaymentMethod: (c.default_payment_method || c.defaultPaymentMethod || 'transfer') as any
      },
        createdAt: new Date(),
        updatedAt: new Date()
      }));
      setAppState(prev => ({ ...prev, companies, currentCompany: prev.currentCompany || (apiCurrentCompany ? {
        id: apiCurrentCompany.id,
        name: apiCurrentCompany.name,
        cif: apiCurrentCompany.cif,
        address: apiCurrentCompany.address || '',
        city: apiCurrentCompany.city || '',
        postalCode: apiCurrentCompany.postal_code || apiCurrentCompany.postalCode || '',
        email: apiCurrentCompany.email || '',
        phone: apiCurrentCompany.phone || '',
        website: apiCurrentCompany.website || '',
        logo: apiCurrentCompany.logo || '',
        isActive: apiCurrentCompany.is_active !== undefined ? !!apiCurrentCompany.is_active : true,
        invoicePrefix: apiCurrentCompany.invoice_prefix || apiCurrentCompany.invoicePrefix || 'FAC',
        currentYear: apiCurrentCompany.current_year || apiCurrentCompany.currentYear || new Date().getFullYear(),
        lastInvoiceNumber: apiCurrentCompany.last_invoice_number || apiCurrentCompany.lastInvoiceNumber || 0,
        fiscalYearStart: apiCurrentCompany.fiscal_year_start || apiCurrentCompany.fiscalYearStart || 1,
        paymentSettings: {
          bankIban: apiCurrentCompany.bank_iban || apiCurrentCompany.bankIban || '',
          bankHolder: apiCurrentCompany.bank_holder || apiCurrentCompany.bankHolder || '',
          bankName: apiCurrentCompany.bank_name || apiCurrentCompany.bankName || '',
          bankSwift: apiCurrentCompany.bank_swift || apiCurrentCompany.bankSwift || '',
          defaultTransferConcept: apiCurrentCompany.default_transfer_concept || apiCurrentCompany.defaultTransferConcept || '',
          defaultBizumPhone: apiCurrentCompany.default_bizum_phone || apiCurrentCompany.defaultBizumPhone || '',
          defaultCardText: apiCurrentCompany.default_card_text || apiCurrentCompany.defaultCardText || 'Pagado con tarjeta',
          defaultPaymentMethod: (apiCurrentCompany.default_payment_method || apiCurrentCompany.defaultPaymentMethod || 'transfer') as any
        },
        createdAt: new Date(),
        updatedAt: new Date()
      } as Company : prev.currentCompany) }));
    } catch (e) {
      console.error('Error cargando empresas', e);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadUsers();
      loadCompanies();
      if (appState.currentCompany?.id) {
        loadProjects(appState.currentCompany.id);
        loadCompanyBillingData(appState.currentCompany.id);
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && appState.currentCompany?.id) {
      loadCompanyBillingData(appState.currentCompany.id, currentProjectId);
    }
  }, [currentProjectId]);

  // Cargar datos cuando se abre el modal de Gestión de Usuarios
  useEffect(() => {
    if (showUserManagement && isAuthenticated) {
      loadUsers();
      loadCompanies();
    }
  }, [showUserManagement, isAuthenticated]);

  // ===== Handlers de Usuarios (CRUD) =====
  const handleCreateUser = async (userData: Omit<User, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const payload = {
        name: userData.name,
        email: userData.email,
        password: userData.password,
        role: userData.role,
        companyId: userData.companyId,
        isActive: userData.isActive,
        authorizedCompanyIds: userData.authorizedCompanyIds || []
      };
      const res = await apiService.createUser(payload as any);
      if ((res as any).error) { alert(res.error); return; }
      await loadUsers();
    } catch (e) {
      console.error('Error creando usuario', e);
      alert('Error creando usuario');
    }
  };

  const handleUpdateUser = async (userId: string, userData: Partial<User>) => {
    try {
      const payload: any = { ...userData };
      if (payload.password === '') delete payload.password;
      const res = await apiService.updateUser(userId, payload);
      if ((res as any).error) { alert(res.error); return; }
      await loadUsers();
    } catch (e) {
      console.error('Error actualizando usuario', e);
      alert('Error actualizando usuario');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      const res = await apiService.deleteUser(userId);
      if ((res as any).error) { alert(res.error); return; }
      await loadUsers();
    } catch (e) {
      console.error('Error eliminando usuario', e);
      alert('Error eliminando usuario');
    }
  };

  // ===== Handlers de Empresas (CRUD) =====
  const handleCreateCompany = async (companyData: Omit<Company, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const res = await apiService.createCompany(companyData as any);
      if ((res as any).error) { alert(res.error); return; }
      await loadCompanies();
    } catch (e) {
      console.error('Error creando empresa', e);
      alert('Error creando empresa');
    }
  };

  const handleUpdateCompany = async (companyId: string, companyData: Partial<Company>) => {
    try {
      const res = await apiService.updateCompany(companyId, companyData as any);
      if ((res as any).error) { alert(res.error); return; }
      await loadCompanies();
    } catch (e) {
      console.error('Error actualizando empresa', e);
      alert('Error actualizando empresa');
    }
  };

  const handleDeleteCompany = async (companyId: string) => {
    try {
      const res = await apiService.deleteCompany(companyId);
      if ((res as any).error) { alert(res.error); return; }
      await loadCompanies();
    } catch (e) {
      console.error('Error eliminando empresa', e);
      alert('Error eliminando empresa');
    }
  };

  const handleSwitchCompany = async (companyId: string) => {
    try {
      const resp = await apiService.activateCompany(companyId);
      if ((resp as any).error) {
        alert((resp as any).error || 'No se pudo activar la empresa');
      }
      await loadCompanies();
    } catch (e) {
      console.error('Error activando empresa', e);
      await loadCompanies();
    }
  };

  // Function to close all modals
  const closeAllModals = () => {
    setShowInvoiceForm(false);
    setShowAISettings(false);
    setShowSettings(false);
    setPreviewInvoice(null);
    setEditingInvoice(null);
  };

  // Invoice management
  const handleAddInvoice = () => {
    setEditingInvoice(null);
    setShowInvoiceForm(true);
  };

  const handleEditInvoice = (invoice: Invoice) => {
    // Normalizar fechas a Date por seguridad
    const safeInvoice: Invoice = {
      ...invoice,
      date: new Date((invoice as any).date),
      dueDate: invoice.dueDate ? new Date((invoice as any).dueDate) : undefined,
    } as any;
    setEditingInvoice(safeInvoice);
    setShowInvoiceForm(true);
  };

  const handleDeleteInvoice = async (invoiceId: string) => {
    const previousInvoices = appState.invoices;
    setAppState(prev => ({
      ...prev,
      invoices: prev.invoices.filter(inv => inv.id !== invoiceId)
    }));

    try {
      const response = await apiService.deleteInvoice(invoiceId);
      if (response.error) {
        setAppState(prev => ({ ...prev, invoices: previousInvoices }));
        alert(`${response.message || response.error}`);
        return;
      }
      await loadCompanyBillingData();
    } catch (error) {
      setAppState(prev => ({ ...prev, invoices: previousInvoices }));
      console.error('Error al eliminar la factura:', error);
      alert('Error al eliminar la factura. Por favor, inténtalo de nuevo.');
    }
  };

  const handleBulkDeleteInvoices = async (invoiceIds: string[]): Promise<BulkDeleteResult> => {
    const previousInvoices = appState.invoices;
    const idSet = new Set(invoiceIds);

    setAppState(prev => ({
      ...prev,
      invoices: prev.invoices.filter(inv => !idSet.has(inv.id))
    }));

    try {
      const response = await apiService.bulkDeleteInvoices(invoiceIds);
      if (response.error) {
        setAppState(prev => ({ ...prev, invoices: previousInvoices }));
        return {
          deleted: [],
          failed: invoiceIds.map((id) => ({
            id,
            error: response.error || 'Error',
            message: response.message || 'No se pudo eliminar'
          })),
          message: response.message
        };
      }

      const data = response.data as BulkDeleteResult;
      const deleted = data?.deleted || [];
      const failed = data?.failed || [];

      if (failed.length > 0) {
        const failedIds = new Set(failed.map((f) => f.id));
        const restored = previousInvoices.filter((inv) => failedIds.has(inv.id));
        setAppState(prev => ({
          ...prev,
          invoices: [...prev.invoices, ...restored]
        }));
      }

      await loadCompanyBillingData();

      return {
        deleted,
        failed,
        message: data?.message
      };
    } catch (error) {
      setAppState(prev => ({ ...prev, invoices: previousInvoices }));
      console.error('Error al eliminar facturas:', error);
      throw error;
    }
  };

  const handleMarkAsPaid = async (invoiceId: string) => {
    try {
      const response = await apiService.updateInvoiceStatus(invoiceId, 'paid');
      if (response.error) {
        alert(`Error al marcar como pagada: ${response.message || response.error}`);
        return;
      }

      setAppState(prev => ({
        ...prev,
        invoices: prev.invoices.map(inv =>
          inv.id === invoiceId
            ? { ...inv, status: 'paid' as const, updatedAt: new Date() }
            : inv
        )
      }));
    } catch (error) {
      console.error('Error al marcar como pagada:', error);
      alert('Error al marcar como pagada. Por favor, inténtalo de nuevo.');
    }
  };

  const handleBulkUpdateInvoiceStatus = async (
    invoiceIds: string[],
    status: 'draft' | 'sent' | 'paid' | 'overdue'
  ) => {
    const previousInvoices = appState.invoices;
    const idSet = new Set(invoiceIds);

    setAppState(prev => ({
      ...prev,
      invoices: prev.invoices.map(inv =>
        idSet.has(inv.id) ? { ...inv, status, updatedAt: new Date() } : inv
      )
    }));

    try {
      const response = await apiService.bulkUpdateInvoiceStatus(invoiceIds, status);
      if (response.error) {
        setAppState(prev => ({ ...prev, invoices: previousInvoices }));
        throw new Error(response.message || response.error);
      }

      const data = response.data;
      const failed = data?.failed || [];
      if (failed.length > 0) {
        const failedIds = new Set(failed.map((f) => f.id));
        setAppState(prev => ({
          ...prev,
          invoices: prev.invoices.map(inv =>
            failedIds.has(inv.id)
              ? previousInvoices.find((p) => p.id === inv.id) || inv
              : inv
          )
        }));
      }

      await loadCompanyBillingData();
      return data;
    } catch (error) {
      setAppState(prev => ({ ...prev, invoices: previousInvoices }));
      throw error;
    }
  };

  const handleBulkAssignProject = async (invoiceIds: string[], projectId: string | null) => {
    const previousInvoices = appState.invoices;
    const idSet = new Set(invoiceIds);
    setAppState(prev => ({
      ...prev,
      invoices: prev.invoices.map(inv =>
        idSet.has(inv.id) ? { ...inv, projectId: projectId ?? undefined } : inv
      )
    }));
    try {
      const response = await apiService.bulkAssignProject(invoiceIds, projectId);
      if (response.error) {
        setAppState(prev => ({ ...prev, invoices: previousInvoices }));
        throw new Error(response.message || response.error);
      }
      await loadCompanyBillingData();
      return response.data;
    } catch (error) {
      setAppState(prev => ({ ...prev, invoices: previousInvoices }));
      throw error;
    }
  };

  const handleBulkAssignGroups = async (invoiceIds: string[], groupIds: string[]) => {
    try {
      const response = await apiService.bulkAssignGroups(invoiceIds, groupIds);
      if (response.error) {
        throw new Error(response.message || response.error);
      }
      await loadCompanyBillingData();
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const handleInvoiceSubmit = async (invoiceData: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      if (editingInvoice) {
        const currentCompany = appState.currentCompany;
        if (!currentCompany) {
          alert('No hay empresa seleccionada');
          return;
        }

        const updatedInvoice: Invoice = {
          ...editingInvoice,
          ...invoiceData,
          projectId: (invoiceData as Invoice).projectId,
          updatedAt: new Date()
        };

        const payload = {
          ...buildInvoiceApiPayload(invoiceData as any, updatedInvoice.displayNumber || String(updatedInvoice.number)),
          projectId: updatedInvoice.projectId,
          groupIds: (invoiceData as Invoice).groupIds || [],
          status: updatedInvoice.status,
        };

        const response = await apiService.updateInvoice(editingInvoice.id, payload);
        if (response.error) {
          alert(`Error al actualizar la factura: ${response.error}`);
          return;
        }

        if (updatedInvoice.status !== editingInvoice.status) {
          const statusRes = await apiService.updateInvoiceStatus(
            editingInvoice.id,
            updatedInvoice.status
          );
          if (statusRes.error) {
            alert(`Factura guardada pero no se pudo cambiar el estado: ${statusRes.message || statusRes.error}`);
          }
        }

        const savedGroups = (response.data as any)?.invoice?.groups;
        if (savedGroups) updatedInvoice.groups = savedGroups;
        setAppState(prev => ({
          ...prev,
          invoices: prev.invoices.map(inv => inv.id === editingInvoice.id ? updatedInvoice : inv)
        }));
      } else {
                // Create new invoice
        const currentCompany = appState.currentCompany;
        if (!currentCompany) {
          alert('No hay empresa seleccionada');
          return;
        }

        // Obtener el siguiente número formateado desde el backend
        const nextNumberResp = await apiService.getNextInvoiceNumber();
        if (nextNumberResp.error || !nextNumberResp.data) {
          alert(`No se pudo obtener el siguiente número de factura`);
          return;
        }
        const formattedNumber = (nextNumberResp.data as any).formattedNumber as string;
        const nextNumber = (nextNumberResp.data as any).nextNumber as number;

        // Construir payload que espera el backend
        const payload = {
          ...buildInvoiceApiPayload(invoiceData as any, formattedNumber),
          projectId: (invoiceData as Invoice).projectId || currentProjectId,
          groupIds: (invoiceData as Invoice).groupIds || [],
        };

        // Guardar en la base de datos
        const response = await apiService.createInvoice(payload as any);
        if (response.error || !response.data) {
          alert(`Error al crear la factura: ${response.error || 'desconocido'}`);
          return;
        }

        // Adaptar la respuesta del backend al tipo del frontend para mantener estado
        const created = (response.data as any).invoice;
        const firstItem = payload.items[0];
        const savedInvoice: Invoice = {
          id: created.id,
          number: typeof created.number === 'string' ? parseInt(String(created.number).split('-').pop() || '0', 10) : created.number,
          date: new Date(created.date),
          clientId: payload.clientId,
          client: (invoiceData as any).client,
          companyId: currentCompany.id,
          projectId: (invoiceData as Invoice).projectId || payload.projectId || null,
          groupIds: (invoiceData as Invoice).groupIds || payload.groupIds || [],
          groups: created.groups || [],
          description: firstItem?.description || invoiceData.description,
          unitPrice: firstItem?.unitPrice ?? invoiceData.unitPrice,
          quantity: firstItem?.quantity ?? invoiceData.quantity,
          subtotal: created.subtotal,
          ivaPercentage: invoiceData.ivaPercentage,
          ivaAmount: created.iva_amount ?? created.ivaAmount ?? invoiceData.ivaAmount,
          irpfPercentage: invoiceData.irpfPercentage,
          irpfAmount: created.irpf_amount ?? created.irpfAmount ?? invoiceData.irpfAmount,
          total: created.total,
          status: created.status as any,
          dueDate: created.due_date ? new Date(created.due_date) : undefined,
          notes: created.notes || invoiceData.notes,
          paymentMethod: (invoiceData as any).paymentMethod,
          bankIban: (invoiceData as any).bankIban,
          bankHolder: (invoiceData as any).bankHolder,
          bankName: (invoiceData as any).bankName,
          paymentNotes: (invoiceData as any).paymentNotes,
          createdAt: new Date(created.created_at || created.createdAt || Date.now()),
          updatedAt: new Date(created.updated_at || created.updatedAt || Date.now())
        } as any;

        // Actualizar estado local y numeración
        setAppState(prev => ({
          ...prev,
          invoices: [...prev.invoices, savedInvoice],
          companies: prev.companies.map(company => 
            company.id === currentCompany.id 
              ? { ...company, lastInvoiceNumber: nextNumber, currentYear: new Date().getFullYear() }
              : company
          ),
          currentCompany: prev.currentCompany?.id === currentCompany.id 
            ? { ...prev.currentCompany, lastInvoiceNumber: nextNumber, currentYear: new Date().getFullYear() }
            : prev.currentCompany
        }));

        setNextInvoiceNumber(nextNumber + 1);
      }
      
      setShowInvoiceForm(false);
      setEditingInvoice(null);
    } catch (error) {
      console.error('Error al guardar la factura:', error);
      alert('Error al guardar la factura. Por favor, inténtalo de nuevo.');
    }
  };

  // Client management
  const handleCreateClientFromInvoice = () => {
    setShowInvoiceForm(false);
    setActiveTab('clients');
  };

  const handleAddClient = async (clientData: Omit<Client, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const currentCompany = appState.currentCompany;
      if (!currentCompany) {
        alert('No hay empresa seleccionada');
        return;
      }

      const newClient: Client = {
        ...clientData,
        companyId: currentCompany.id,
        id: `client_${Date.now()}`,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      // Guardar en la base de datos
      const response = await apiService.createClient(newClient);
      if (response.error) {
        alert(`Error al crear el cliente: ${response.error}`);
        return;
      }
      
      // Si se guardó correctamente en la BD, actualizar estado local
      if (response.data && (response.data as any).client) {
        const savedClient = (response.data as any).client as any;
        const clientMapped: Client = {
          id: savedClient.id,
          name: savedClient.name,
          cif: savedClient.cif || '',
          address: savedClient.address || '',
          city: savedClient.city || '',
          email: savedClient.email || '',
          phone: savedClient.phone || '',
          companyId: savedClient.company_id || currentCompany.id,
          createdAt: new Date(savedClient.created_at || Date.now()),
          updatedAt: new Date(savedClient.updated_at || Date.now())
        };
        setAppState(prev => ({
          ...prev,
          clients: [...prev.clients, clientMapped]
        }));
      }
    } catch (error) {
      console.error('Error al crear el cliente:', error);
      alert('Error al crear el cliente. Por favor, inténtalo de nuevo.');
    }
  };

  const handleEditClient = async (updatedClient: Client) => {
    try {
      // Actualizar en la base de datos
      const response = await apiService.updateClient(updatedClient.id, updatedClient);
      if (response.error) {
        alert(`Error al actualizar el cliente: ${response.error}`);
        return;
      }
      
      // Si se actualizó correctamente en la BD, actualizar estado local
      setAppState(prev => ({
        ...prev,
        clients: prev.clients.map(client => 
          client.id === updatedClient.id ? updatedClient : client
        )
      }));
    } catch (error) {
      console.error('Error al actualizar el cliente:', error);
      alert('Error al actualizar el cliente. Por favor, inténtalo de nuevo.');
    }
  };

  const handleDeleteClient = async (clientId: string) => {
    try {
      // Check if client is used in invoices
      const clientInUse = appState.invoices.some(invoice => invoice.client.id === clientId);
      
      if (clientInUse) {
        alert('No se puede eliminar este cliente porque tiene facturas asociadas. Elimina primero las facturas.');
        return;
      }
      
      // Eliminar de la base de datos
      const response = await apiService.deleteClient(clientId);
      if (response.error) {
        alert(`Error al eliminar el cliente: ${response.error}`);
        return;
      }
      
      // Si se eliminó correctamente de la BD, actualizar estado local
      setAppState(prev => ({
        ...prev,
        clients: prev.clients.filter(client => client.id !== clientId)
      }));
    } catch (error) {
      console.error('Error al eliminar el cliente:', error);
      alert('Error al eliminar el cliente. Por favor, inténtalo de nuevo.');
    }
  };

  // Creación rápida de cliente desde el formulario de factura
  const handleQuickCreateClient = async (payload: { name: string; cif?: string; address?: string; city?: string; email?: string; phone?: string; }): Promise<Client | null> => {
    try {
      const currentCompany = appState.currentCompany;
      if (!currentCompany) {
        alert('No hay empresa seleccionada');
        return null;
      }
      const response = await apiService.createClient({ ...payload, companyId: currentCompany.id } as any);
      if (response.error || !response.data) {
        alert(response.error || 'No se pudo crear el cliente');
        return null;
      }
      const saved = (response.data as any).client || response.data;
      const newClient: Client = {
        id: saved.id,
        name: saved.name,
        cif: saved.cif || '',
        address: saved.address || '',
        city: saved.city || '',
        email: saved.email || '',
        phone: saved.phone || '',
        companyId: saved.company_id || currentCompany.id,
        createdAt: new Date(saved.created_at || Date.now()),
        updatedAt: new Date(saved.updated_at || Date.now())
      };
      setAppState(prev => ({ ...prev, clients: [...prev.clients, newClient] }));
      return newClient;
    } catch (e) {
      console.error('Error quick create client', e);
      alert('Error creando cliente');
      return null;
    }
  };

  // Backup/Restore
  const handleRestore = (backupData: BackupData) => {
    setAppState(prev => ({
      ...prev,
      currentCompany: backupData.company,
      companies: prev.companies.some(c => c.id === backupData.company.id) 
        ? prev.companies.map(c => c.id === backupData.company.id ? backupData.company : c)
        : [...prev.companies, backupData.company],
      users: [
        ...prev.users.filter(u => u.companyId !== backupData.company.id),
        ...backupData.users
      ],
      invoices: backupData.invoices,
      clients: backupData.clients
    }));
    
    // Update next invoice number
    const maxInvoiceNumber = Math.max(0, ...backupData.invoices.map(inv => inv.number));
    setNextInvoiceNumber(maxInvoiceNumber + 1);
  };



  const handleViewInvoice = async (invoice: Invoice) => {
    const cid = appState.currentCompany?.id || invoice.companyId;
    try {
      const res = await apiService.getInvoice(invoice.id);
      if (!res.error && res.data) {
        setPreviewInvoice(mapDetailInvoice(res.data, appState.clients, cid));
        return;
      }
    } catch (e) {
      console.error('Error cargando detalle de factura', e);
    }
    setPreviewInvoice(invoice);
  };

  // Check authentication: memoria AuthService o JWT en localStorage
  useEffect(() => {
    const session = AuthService.getSession();
    if (session && session.isAuthenticated) {
      setCurrentUser(session.user);
      setCurrentCompany(session.company);
      setIsAuthenticated(true);
      const remembered = localStorage.getItem('dashboard_remember_me');
      setRememberMe(remembered === 'true');
      return;
    }
    if (!apiService.isAuthenticated()) return;
    apiService.getProfile().then((res: any) => {
      const prof = res?.data?.user;
      if (!prof) return;
      const company = prof.company
        ? ({
            id: prof.company.id,
            name: prof.company.name,
            cif: prof.company.cif || '',
            address: '',
            city: '',
            postalCode: '',
            email: '',
            isActive: true,
            invoicePrefix: 'FAC',
            currentYear: new Date().getFullYear(),
            lastInvoiceNumber: 0,
            fiscalYearStart: 1,
            phone: '',
            website: '',
            logo: '',
            createdAt: new Date(),
            updatedAt: new Date(),
          } as Company)
        : null;
      const userObj: User = {
        id: prof.id,
        email: prof.email,
        name: prof.username || prof.email,
        password: '',
        role: prof.role,
        companyId: prof.company?.id || '',
        isActive: true,
        mustChangePassword: false,
        createdAt: new Date(),
        updatedAt: new Date(),
      } as User;
      setCurrentUser(userObj);
      setCurrentCompany(company);
      setAppState((prev) => ({
        ...prev,
        currentUser: userObj,
        currentCompany: company,
        isAuthenticated: true,
      }));
      setIsAuthenticated(true);
      loadUsers();
      loadCompanies();
      if (company?.id) loadCompanyBillingData(company.id);
    });
  }, []);

  // Load state from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem('dashboard-app-state');
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        setAppState(parsedState);
      } catch (error) {
        console.error('Error loading saved state:', error);
        initializeDefaultState();
      }
    } else {
      initializeDefaultState();
    }
  }, []);

  const showEntikTab = shouldShowEntikRecordsTab(
    appState.currentCompany,
    currentProjectId,
    projects,
    appState.currentUser?.email,
    appState.currentUser?.role
  );

  const showKitDigitalTab = shouldShowKitDigitalTab(
    appState.currentCompany,
    appState.currentUser?.email,
    appState.currentUser?.role
  );

  useEffect(() => {
    if (!isAuthenticated) return;
    if (activeTab === 'entik-reservas' && !showEntikTab) {
      setActiveTab('dashboard');
    }
    if (activeTab === 'kit-digital' && !showKitDigitalTab) {
      setActiveTab('dashboard');
    }
    if (activeTab === 'woocommerce' && !wooCommerceEnabled) {
      setActiveTab('dashboard');
    }
  }, [isAuthenticated, activeTab, showEntikTab, showKitDigitalTab, wooCommerceEnabled]);

  useEffect(() => {
    if (!isAuthenticated || !appState.currentCompany?.id) return;
    (async () => {
      const res = await apiService.getWooCommerceSettings();
      const d = res.data as { enabled?: boolean };
      setWooCommerceEnabled(!!d?.enabled);
    })();
  }, [isAuthenticated, appState.currentCompany?.id]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    const st = params.get("wc_oauth_state");
    const store = params.get("wc_store");
    if (st) {
      setWcOauthState(st);
      setActiveTab("settings");
      if (store) setWcOauthStore(store);
      params.delete("wc_oauth_state");
      params.delete("wc_store");
      const qs = params.toString();
      window.history.replaceState({}, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
    }
  }, [isAuthenticated]);

  const handleAssistantNavigate = (tab: string) => {
    if (tab === 'settings') {
      setSettingsSection('woocommerce');
      setActiveTab('settings');
      return;
    }
    setActiveTab(tab);
  };

  // Show login if not authenticated
  if (!isAuthenticated) {
    return (
      <Login
        onLoginSuccess={handleLoginSuccess}
        onRememberMeChange={setRememberMe}
      />
    );
  }
  // Get filtered data for current company with authorization
  const isPrivileged = appState.currentUser?.role === 'admin' || appState.currentUser?.role === 'superadmin' || appState.currentUser?.role === 'usuario';
  const isUserAuthorizedForCompany = (companyId: string) => {
    if (isPrivileged) return true;
    const user = appState.currentUser;
    if (!user) return false;
    if (user.companyId === companyId) return true;
    // Simplified: for now, only allow access to own company
    return false;
  };

  const currentCompanyInvoices = appState.invoices.filter(inv => {
    if (!appState.currentCompany || inv.companyId !== appState.currentCompany.id) return false;
    if (currentProjectId && inv.projectId !== currentProjectId) return false;
    return true;
  });
  
  const currentCompanyClients = appState.currentCompany
    ? appState.clients.filter(client => client.companyId === appState.currentCompany!.id)
    : appState.clients;

  // Companies the current user can access
  const availableCompanies = appState.companies.filter((company) => isUserAuthorizedForCompany(company.id));

  const activeProjectName = currentProjectId
    ? projects.find((p) => p.id === currentProjectId)?.name || null
    : null;

  const isSuperadmin = appState.currentUser?.role === 'superadmin';
  const navItems = [
    ...buildDefaultNavItems(showEntikTab, wooCommerceEnabled, showKitDigitalTab),
    ...(isSuperadmin ? [{ id: 'platform-users', name: 'Usuarios plataforma', icon: UserGroupIcon }] : []),
  ];

  const commandActions: CommandAction[] = [
    { id: 'nav-dashboard', label: 'Panel', group: 'Navegación', onSelect: () => setActiveTab('dashboard') },
    { id: 'nav-invoices', label: 'Facturas', group: 'Navegación', onSelect: () => setActiveTab('invoices') },
    { id: 'nav-clients', label: 'Clientes', group: 'Navegación', onSelect: () => setActiveTab('clients') },
    { id: 'nav-email', label: 'Email', group: 'Navegación', onSelect: () => setActiveTab('email') },
    { id: 'nav-settings', label: 'Ajustes', group: 'Navegación', onSelect: () => setActiveTab('settings') },
    { id: 'new-invoice', label: 'Nueva factura', group: 'Acciones', onSelect: () => handleAddInvoice() },
    { id: 'new-expense', label: 'Nuevo gasto', group: 'Acciones', onSelect: () => setActiveTab('work-expenses') },
    { id: 'open-assistant', label: 'Asistente IA', group: 'Acciones', onSelect: () => setAssistantOpen(true) },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <DashboardHome
            company={appState.currentCompany}
            invoices={currentCompanyInvoices}
            clients={currentCompanyClients}
            apiStats={dashboardApiStats || undefined}
            onNavigate={setActiveTab}
            onNewInvoice={handleAddInvoice}
          />
        );

      case 'email':
        return <EmailApp currentUser={currentUser || undefined} />;

      case 'platform-users':
        return appState.currentUser?.role === 'superadmin' ? (
          <PlatformUsersPanel variant="inline" />
        ) : null;

      case 'settings':
        return (
          <div className="max-w-4xl">
            <Settings
              key="settings-inline"
              onClose={() => setActiveTab('dashboard')}
              users={appState.users}
              companies={appState.companies}
              currentUser={appState.currentUser}
              currentCompany={appState.currentCompany}
              onOpenAutoBilling={() => setActiveTab('auto-billing')}
              variant="inline"
              initialSection={wcOauthState ? 'woocommerce' : settingsSection}
              wcOauthState={wcOauthState}
              wcOauthStore={wcOauthStore}
            />
          </div>
        );

      case 'invoices':
        return (
          <InvoiceList
            invoices={currentCompanyInvoices}
            projects={projects}
            globalProjectId={currentProjectId}
            activeProjectName={activeProjectName}
            canDelete={isPrivileged}
            onViewInvoice={handleViewInvoice}
            onEditInvoice={handleEditInvoice}
            onDeleteInvoice={isPrivileged ? handleDeleteInvoice : undefined}
            onBulkDeleteInvoices={isPrivileged ? handleBulkDeleteInvoices : undefined}
            onBulkUpdateStatus={isPrivileged ? handleBulkUpdateInvoiceStatus : undefined}
            onBulkAssignProject={isPrivileged ? handleBulkAssignProject : undefined}
            onBulkAssignGroups={isPrivileged ? handleBulkAssignGroups : undefined}
            onAddInvoice={handleAddInvoice}
            onMarkAsPaid={isPrivileged ? handleMarkAsPaid : undefined}
          />
        );

      case 'clients':
        return (
          <ClientList
            clients={currentCompanyClients}
            onAddClient={handleAddClient}
            onEditClient={handleEditClient}
            currentCompany={appState.currentCompany}
            onDeleteClient={handleDeleteClient}
          />
        );

      case 'work-expenses':
        return <ProfessionalExpensesManager />;
      case 'gestoria':
        return <GestoriaHub />;
      case 'import':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">Importar Facturas</h2>
                <p className="text-zinc-600 dark:text-zinc-400">
                  Arrastra documentos, pulsa «Analizar con IA» y revisa la extracción antes de importar.
                  Opcional: activa importación automática por lotes.
                </p>
              </div>
              <button
                onClick={() => setActiveTab('dashboard')}
                className="px-4 py-2 text-zinc-600 dark:text-zinc-300 hover:text-zinc-800 dark:hover:text-zinc-100 border border-zinc-300 dark:border-zinc-600 rounded-xl hover:bg-zinc-50 dark:hover:bg-zinc-800"
              >
                ← Volver al Dashboard
              </button>
            </div>
            <ImportDocumentsDropzone
              onImported={async () => {
                await loadCompanyBillingData();
                setActiveTab('invoices');
              }}
            />
          </div>
        );

      case 'auto-billing':
        return (
          <AutoBillingManager
            clients={currentCompanyClients}
            currentCompany={appState.currentCompany}
            onInvoiceGenerated={(invoice) => {
              setAppState(prev => ({
                ...prev,
                invoices: [...prev.invoices, invoice]
              }));
              setNextInvoiceNumber(prev => prev + 1);
            }}
            nextInvoiceNumber={nextInvoiceNumber}
          />
        );

      case 'backup':
        return (
          <div className="bg-white shadow rounded-lg p-6">
            <p className="text-gray-600">La sección de Backup se ha movido a Configuración.</p>
          </div>
        );

      case 'woocommerce':
        return <WooCommercePanel />;

      case 'entik-reservas':
        return (
          <EntikRecordsReservas
            currentCompany={appState.currentCompany}
            userEmail={currentUser?.email}
            userRole={currentUser?.role}
          />
        );

      case 'kit-digital':
        return (
          <KitDigitalPanel
            currentCompany={appState.currentCompany}
            userEmail={currentUser?.email}
            userRole={currentUser?.role}
          />
        );

      default:
        return null;
    }
  };

  const handleOpenInvoiceFromAssistant = async (invoiceId: string) => {
    setAssistantOpen(false);
    setActiveTab('invoices');
    const existing = appState.invoices.find((inv) => inv.id === invoiceId);
    if (existing) {
      await handleViewInvoice(existing);
      return;
    }
    const cid = appState.currentCompany?.id || '';
    try {
      const res = await apiService.getInvoice(invoiceId);
      if (!res.error && res.data) {
        setPreviewInvoice(mapDetailInvoice(res.data, appState.clients, cid));
      }
    } catch {
      /* preview opcional si la factura aún no está en caché local */
    }
  };

  return (
    <AssistantProvider>
    <>
    <AppShell
      navItems={navItems}
      activeTab={activeTab}
      onNavigate={setActiveTab}
      currentUser={appState.currentUser}
      companies={availableCompanies}
      currentCompanyId={appState.currentCompany?.id || ''}
      onSwitchCompany={async (id) => {
        setCurrentProjectId(null);
        await handleSwitchCompany(id);
        setAppState((prev) => ({
          ...prev,
          currentCompany: prev.companies.find((c) => c.id === id) || prev.currentCompany,
        }));
        await loadProjects(id);
        await loadCompanyBillingData(id, null);
      }}
      projects={projects.map((p) => ({ id: p.id, name: p.name, color: p.color, slug: p.slug }))}
      currentProjectId={currentProjectId}
      onSwitchProject={(id) => setCurrentProjectId(id)}
      onLogout={handleLogout}
      onNewInvoice={handleAddInvoice}
      onOpenSettings={() => setActiveTab('settings')}
      onOpenEmail={() => setActiveTab('email')}
      onOpenAssistant={() => setAssistantOpen((o) => !o)}
      assistantOpen={assistantOpen}
      commandActions={commandActions}
    >
      {renderContent()}
    </AppShell>

    <FactuAssistantPanel
      open={assistantOpen}
      onClose={() => setAssistantOpen(false)}
      projectId={currentProjectId}
      activeTab={activeTab}
      userName={currentUser?.name?.trim() ? currentUser.name : null}
      companyName={currentCompany?.name || appState.currentCompany?.name || null}
      onOpenInvoice={handleOpenInvoiceFromAssistant}
      onNavigate={handleAssistantNavigate}
    />

      {/* Invoice Form Modal */}
      {showInvoiceForm && (
        <InvoiceForm
          onClose={() => {
            setShowInvoiceForm(false);
            setEditingInvoice(null);
          }}
          onSubmit={handleInvoiceSubmit}
          clients={currentCompanyClients}
          nextInvoiceNumber={nextInvoiceNumber}
          setNextInvoiceNumber={setNextInvoiceNumber}
          editingInvoice={editingInvoice}
          onQuickCreateClient={handleQuickCreateClient}
          companyId={appState.currentCompany?.id || ''}
          projects={projects}
          defaultProjectId={currentProjectId || editingInvoice?.projectId || projects[0]?.id || null}
          invoiceConfig={{
            invoicePrefix: appState.currentCompany?.invoicePrefix || 'FAC',
            currentYear: appState.currentCompany?.currentYear || new Date().getFullYear(),
            lastInvoiceNumber: appState.currentCompany?.lastInvoiceNumber || 0
          }}
          paymentSettings={appState.currentCompany?.paymentSettings}
        />
      )}

      {/* AI Settings Modal */}
      {showAISettings && (
        <AISettings onClose={() => setShowAISettings(false)} />
      )}

      {/* Invoice Preview Modal */}
      {previewInvoice && (
          <InvoicePreview
            invoice={previewInvoice}
            onClose={() => setPreviewInvoice(null)}
            onEdit={(inv) => {
              setPreviewInvoice(null);
              handleEditInvoice(inv);
            }}
          />
      )}

      {/* Settings Modal */}
      {showSettings && (
        <Settings
          key="settings-modal"
          onClose={() => setShowSettings(false)}
          users={appState.users}
          companies={appState.companies}
          currentUser={appState.currentUser}
          currentCompany={appState.currentCompany}
          onOpenAutoBilling={() => setActiveTab('auto-billing')}
          initialSection={wcOauthState ? 'woocommerce' : settingsSection}
          wcOauthState={wcOauthState}
          wcOauthStore={wcOauthStore}
        />
      )}

      {/* User Management Modal */}
      {showUserManagement && (
        <UserManagement
          users={appState.users}
          companies={appState.companies}
          currentUser={appState.currentUser}
          currentCompany={appState.currentCompany}
          onClose={() => setShowUserManagement(false)}
          onCreateUser={handleCreateUser}
          onCreateCompany={handleCreateCompany}
          onUpdateUser={handleUpdateUser}
          onUpdateCompany={handleUpdateCompany}
          onDeleteUser={handleDeleteUser}
          onDeleteCompany={handleDeleteCompany}
          onSwitchCompany={handleSwitchCompany}
          onOpenAISettings={() => setShowAISettings(true)}
          onExportBackup={() => {
            const dataStr = JSON.stringify(appState);
            const blob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `dashboard-backup-${new Date().toISOString().split('T')[0]}.json`;
            link.click();
            URL.revokeObjectURL(url);
          }}
          onImportBackup={(file) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              try {
                const content = e.target?.result as string;
                const importedData = JSON.parse(content);
                setAppState(importedData);
              } catch (error) {
                console.error('Error importing backup:', error);
                alert('Error al importar el respaldo');
              }
            };
            reader.readAsText(file);
          }}
        />
      )}
    </>
    </AssistantProvider>
  );
};

function FactuAssistantPanel({
  open,
  onClose,
  projectId,
  activeTab,
  userName,
  companyName,
  onOpenInvoice,
  onNavigate,
}: {
  open: boolean;
  onClose: () => void;
  projectId: string | null;
  activeTab: string;
  userName: string | null;
  companyName: string | null;
  onOpenInvoice: (invoiceId: string) => void;
  onNavigate: (tab: string) => void;
}) {
  const { selectedExpenseIds } = useAssistant();
  return (
    <FactuAssistant
      open={open}
      onClose={onClose}
      selectedExpenseIds={selectedExpenseIds}
      projectId={projectId}
      activeTab={activeTab}
      userName={userName}
      companyName={companyName}
      onOpenInvoice={onOpenInvoice}
      onNavigate={onNavigate}
    />
  );
}

export default Dashboard;
